import { findNearestTargetAnimal, eatTargetAnimal } from '../entities/bee_eater.js';
import { defineQuery } from 'bitecs';
import { AI, AI_STATE, Animal, Position, ANIMAL_TYPES } from '../world.js';
import { TILE_SIZE, MAP_WIDTH, MAP_HEIGHT } from '../config.js';

// Query for entities with AI components
const aiQuery = defineQuery([AI, Position, Animal]);

// Helper function to get a random position within bounds
function getRandomPosition(minX, maxX, minY, maxY) {
    return {
        x: Math.random() * (maxX - minX) + minX,
        y: Math.random() * (maxY - minY) + minY
    };
}

// AI system to handle entity behavior
export function aiSystem(world, delta) {
    // Ensure delta is a number and scale it appropriately
    const deltaTime = (typeof delta === 'object' ? delta.deltaTime || 16.67 : delta) / 1000; // Convert to seconds

    const entities = aiQuery(world);

    const ticksPerDay = 1000; // Define how many ticks represent a game day for energy calc

    for (const entity of entities) {
        const predatorType = Animal.type[entity];
        const animalConfig = Object.values(ANIMAL_TYPES).find(type => type.id === predatorType);

        // --- Energy Consumption ---
        if (animalConfig && animalConfig.dailyEnergyRequirement > 0) {
            const energyConsumedPerTick = animalConfig.dailyEnergyRequirement / ticksPerDay;
            Animal.energy[entity] = Math.max(0, Animal.energy[entity] - energyConsumedPerTick);
        }
        // --- End Energy Consumption ---

        let preyTypeId = null;

        // Determine prey type based on predator type
        if (predatorType === ANIMAL_TYPES.BEE_EATER.id) {
            preyTypeId = ANIMAL_TYPES.BEE.id;
        } else if (predatorType === ANIMAL_TYPES.HAWK.id) {
            preyTypeId = ANIMAL_TYPES.BEE_EATER.id;
        } else {
            continue; // Skip other animals handled by animalBehaviorSystem
        }

        const predatorTypeName = Object.keys(ANIMAL_TYPES).find(k => ANIMAL_TYPES[k].id === predatorType);
        const preyTypeName = Object.keys(ANIMAL_TYPES).find(k => ANIMAL_TYPES[k].id === preyTypeId);

        // Update timers
        if (AI.changeDirectionTimer[entity] > 0) {
            AI.changeDirectionTimer[entity] = Math.max(0, AI.changeDirectionTimer[entity] - deltaTime);
        }

        // Check energy level and update state if needed
        // Use animalConfig.maxEnergy which should be defined in ANIMAL_TYPES
        const maxEnergy = animalConfig ? animalConfig.maxEnergy : 100; // Fallback if config not found
        const energyPercent = (Animal.energy[entity] / maxEnergy) * 100;

        if (energyPercent < 50 && AI.state[entity] !== AI_STATE.HUNTING) {
            AI.state[entity] = AI_STATE.HUNTING;
        } else if (energyPercent >= 50 && AI.state[entity] === AI_STATE.HUNTING) {
             AI.state[entity] = AI_STATE.RESTING;
        }

        // Handle different AI states
        switch (AI.state[entity]) {
            case AI_STATE.HUNTING:
                if (energyPercent >= 50) {
                    AI.state[entity] = AI_STATE.RESTING;
                    break;
                }

                const nearestTarget = findNearestTargetAnimal(world, Position.x[entity], Position.y[entity], preyTypeId);

                if (!nearestTarget) {
                    // Random movement if no target
                    const randomAngle = Math.random() * Math.PI * 2;
                    const searchDistance = TILE_SIZE * 5;
                    AI.targetX[entity] = Position.x[entity] + Math.cos(randomAngle) * searchDistance;
                    AI.targetY[entity] = Position.y[entity] + Math.sin(randomAngle) * searchDistance;
                } else {
                    // Target found, update target position
                    AI.targetX[entity] = Position.x[nearestTarget];
                    AI.targetY[entity] = Position.y[nearestTarget];

                    const huntDx = AI.targetX[entity] - Position.x[entity];
                    const huntDy = AI.targetY[entity] - Position.y[entity];
                    const huntDistance = Math.sqrt(huntDx * huntDx + huntDy * huntDy);

                    // Check if caught
                    if (huntDistance < TILE_SIZE) {
                        if (world.entities.includes(nearestTarget) && Animal.type[nearestTarget] === preyTypeId) {
                             const success = eatTargetAnimal(world, entity, nearestTarget);
                             if (success) {
                                 AI.state[entity] = AI_STATE.EATING;
                                 AI.timer[entity] = 1.0; // 1 second eating animation
                                 // Replenish some energy on successful hunt
                                 const energyGained = animalConfig ? animalConfig.energyPerFood * 0.5 : 10; // Example: gain 50% of food value
                                 Animal.energy[entity] = Math.min(maxEnergy, Animal.energy[entity] + energyGained);
                             }
                         } else {
                            // Target invalid, move randomly
                            const randomAngle = Math.random() * Math.PI * 2;
                            const searchDistance = TILE_SIZE * 5;
                            AI.targetX[entity] = Position.x[entity] + Math.cos(randomAngle) * searchDistance;
                            AI.targetY[entity] = Position.y[entity] + Math.sin(randomAngle) * searchDistance;
                         }
                    }
                }

                // Always move towards target when hunting
                const moveDx = AI.targetX[entity] - Position.x[entity];
                const moveDy = AI.targetY[entity] - Position.y[entity];
                const moveDistance = Math.sqrt(moveDx * moveDx + moveDy * moveDy);

                if (moveDistance > 1) { // Avoid division by zero or tiny movements
                    const huntBaseSpeed = AI.speed[entity] * TILE_SIZE * 120; // Speed factor
                    const huntSpeed = huntBaseSpeed * deltaTime;

                    const moveX = (moveDx / moveDistance) * huntSpeed;
                    const moveY = (moveDy / moveDistance) * huntSpeed;

                    // Clamp position to map boundaries
                    Position.x[entity] = Math.max(0, Math.min(MAP_WIDTH * TILE_SIZE, Position.x[entity] + moveX));
                    Position.y[entity] = Math.max(0, Math.min(MAP_HEIGHT * TILE_SIZE, Position.y[entity] + moveY));

                    // Update sprite position and flip
                    if (world.sprites && world.sprites[entity]) {
                        world.sprites[entity].position.set(Position.x[entity], Position.y[entity]);
                        if (moveDx !== 0) {
                            // Preserve original scale magnitude, only flip direction
                            world.sprites[entity].scale.x = Math.abs(world.sprites[entity].scale.x) * Math.sign(moveDx);
                        }
                    }
                }
                break;

            case AI_STATE.RESTING:
                // Stay still and recover energy
                AI.targetX[entity] = Position.x[entity];
                AI.targetY[entity] = Position.y[entity];
                // Use maxEnergy from config
                Animal.energy[entity] = Math.min(Animal.energy[entity] + (deltaTime * 5), maxEnergy);
                break;

            case AI_STATE.EATING:
                if (AI.timer[entity] > 0) {
                    AI.timer[entity] = Math.max(0, AI.timer[entity] - deltaTime);
                } else {
                    // Decide next state based on energy
                    // Re-calculate energyPercent after potential gain from eating
                    const currentEnergyPercent = (Animal.energy[entity] / maxEnergy) * 100;
                    AI.state[entity] = (currentEnergyPercent < 50) ? AI_STATE.HUNTING : AI_STATE.RESTING;
                }
                break;
        } // End switch
    } // End for loop

    return world;
} // End function
